import { useEffect, useState } from 'react'

import { Button, Group, Loader, Slider, Stack, Text } from '@mantine/core'

const CycleToInitialWaitTimeMs = 4000

export interface SlideApprovalControlProps {
  onApprove?: () => Promise<void>
  onStartApprove?: () => void
  onCancel?: () => void
  disabled?: boolean
  confirmationText?: string
  buttonText?: string
  successText?: string
  error?: string | null
  setError?: (error: string | null) => void
}

export function SlideApprovalControl({
  onApprove,
  onStartApprove,
  onCancel,
  disabled = false,
  confirmationText = 'Slide to confirm',
  buttonText = 'Approve',
  successText = 'Approved successfully',
  error,
  setError,
}: SlideApprovalControlProps) {
  const [phase, setPhase] = useState<
    'initial' | 'confirm' | 'processing' | 'complete'
  >('initial')

  const [sliderValue, setSliderValue] = useState(0)
  const [cycling, setCycling] = useState(false)

  const handleInitialClick = (): void => {
    if (setError) {
      setError(null)
    }
    setPhase('confirm')
    setSliderValue(0)
    if (onStartApprove) {
      onStartApprove()
    }
  }

  useEffect(() => {
    if (cycling) {
      const toInitial = setTimeout(() => {
        setPhase('initial')
        setCycling(false)
      }, CycleToInitialWaitTimeMs)

      return () => {
        clearTimeout(toInitial)
      }
    }
  }, [cycling])

  const handleCancel = (): void => {
    setPhase('initial')

    if (onCancel) {
      onCancel()
    }
  }

  const handleConfirmation = async (): Promise<void> => {
    setPhase('processing')

    if (onApprove) {
      try {
        await onApprove()
        setPhase('complete')
        setCycling(true)
      } catch (err) {
        let msg = 'An error occurred while processing your request.'
        if (err instanceof Error) {
          msg = err.message
        } else if (typeof err === 'string') {
          msg = err
        } else if (err && typeof err === 'object' && 'message' in err) {
          msg = (err as { message: string }).message
        }
        console.error('Error approving:', err)
        if (setError) setError(msg)
        handleCancel()
      }
    } else {
      console.error('onApprove function is not provided')
      setPhase('complete')
      setCycling(true)
    }
  }

  const handleSliderComplete = (value: number): void => {
    if (value === 100) {
      void handleConfirmation()
    }
  }

  return (
    <Stack>
      {phase === 'initial' && (
        <Button onClick={handleInitialClick} disabled={disabled}>
          {buttonText}
        </Button>
      )}

      {phase === 'confirm' && (
        <Stack>
          <Text size="sm">{confirmationText}</Text>
          <Group align="center">
            <div style={{ flex: 1 }}>
              <Slider
                value={sliderValue}
                onChange={setSliderValue}
                marks={[{ value: 100, label: 'Confirm' }]}
                onChangeEnd={handleSliderComplete}
                size="md"
              />
            </div>
            <Button variant="subtle" size="xs" onClick={handleCancel}>
              Cancel
            </Button>
          </Group>
        </Stack>
      )}

      {phase === 'processing' && (
        <Group>
          <Loader size="sm" />
          <Text size="sm">Processing...</Text>
        </Group>
      )}

      {phase === 'complete' && (
        <Stack>
          <Text c="green" size="sm">
            {successText}
          </Text>
        </Stack>
      )}

      {error && (
        <Text c="red" size="sm">
          {error}
        </Text>
      )}
    </Stack>
  )
}
